import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button } from 'antd';
import type { MenuProps } from 'antd';
// Icons (reused from MUI — they are plain React nodes that antd accepts)
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import MainLogo from '../../components/MainLogo';
import { PermissionEnum } from '../../enums/permission';
import { RouteNameEnum } from '../../enums/routeName';
import useAuth from '../../hooks/useAuth';
import usePermission from '../../hooks/usePermission';
import { BBA_TERTIARY, NEUTRAL_200 } from '../../themes/tokens';

const { Sider, Content } = Layout;

type MenuConfig = {
  key?: string;
  type: 'menu' | 'section';
  icon?: React.ReactNode;
  title: string;
  to?: string;
  permission?: string[];
};

const menu: MenuConfig[] = [
  {
    key: RouteNameEnum.dashboard.index,
    type: 'menu',
    icon: <HomeOutlinedIcon fontSize="small" />,
    title: 'Home',
    to: RouteNameEnum.dashboard.index,
  },
  {
    key: 'sectionContentManagement',
    type: 'section',
    title: 'Content management',
    permission: [
      PermissionEnum.code.eventViewList,
      PermissionEnum.code.featuredContentViewList,
      PermissionEnum.code.newsViewList,
      PermissionEnum.code.notificationViewList,
    ],
  },
  {
    type: 'menu',
    icon: <EventIcon fontSize="small" />,
    title: 'Events',
    to: RouteNameEnum.event.index,
    permission: [PermissionEnum.code.eventViewList],
  },
  {
    type: 'menu',
    icon: <NewspaperIcon fontSize="small" />,
    title: 'News',
    to: RouteNameEnum.news.index,
    permission: [PermissionEnum.code.newsViewList],
  },
  {
    type: 'menu',
    icon: <ViewCarouselIcon fontSize="small" />,
    title: 'Featured',
    to: RouteNameEnum.featuredContent.index,
    permission: [PermissionEnum.code.featuredContentViewList],
  },
  {
    type: 'menu',
    icon: <NotificationsIcon fontSize="small" />,
    title: 'Notification',
    to: RouteNameEnum.notification.index,
    permission: [PermissionEnum.code.notificationViewList],
  },
  {
    key: 'sectionUserManagement',
    type: 'section',
    title: 'Customer management',
    permission: [PermissionEnum.code.customerViewList],
  },
  {
    type: 'menu',
    icon: <PeopleIcon fontSize="small" />,
    title: 'Customer account',
    to: RouteNameEnum.customer.index,
    permission: [PermissionEnum.code.customerViewList],
  },
  {
    key: 'sectionAdminManagement',
    type: 'section',
    title: 'Administrator management',
    permission: [PermissionEnum.code.adminViewList],
  },
  {
    key: RouteNameEnum.boUser.index,
    type: 'menu',
    icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
    title: 'Admin account',
    to: RouteNameEnum.boUser.index,
    permission: [PermissionEnum.code.adminViewList],
  },
];

const DRAWER_KEY = 'drawerOpen';

const DefaultLayout: React.FC = (): React.ReactElement => {
  const { authAction } = useAuth();
  const { checkPermission } = usePermission();
  const navigate = useNavigate();
  const location = useLocation();

  const defaultOpen = localStorage.getItem(DRAWER_KEY) === 'true';
  const [collapsed, setCollapsed] = React.useState(!defaultOpen);

  React.useEffect(() => {
    localStorage.setItem(DRAWER_KEY, String(!collapsed));
  }, [collapsed]);

  const visible = (perm?: string[]): boolean => !perm || checkPermission(perm);

  // Build antd Menu items, grouping each section with the menu items that
  // follow it, and filtering everything by the user's permissions.
  const items: MenuProps['items'] = [];
  let bucket: MenuProps['items'] = [];
  let section: MenuConfig | null = null;

  const flush = (): void => {
    const children = (bucket || []).filter(Boolean);
    if (children.length === 0) { bucket = []; return; }
    if (section && visible(section.permission)) {
      items?.push({ type: 'group', label: section.title, children });
    } else if (!section) {
      items?.push(...children);
    }
    bucket = [];
  };

  menu.forEach((item) => {
    if (item.type === 'section') {
      flush();
      section = item;
      return;
    }
    if (visible(item.permission)) {
      bucket?.push({
        key: item.to || item.key || item.title,
        icon: item.icon,
        label: item.title,
      });
    }
  });
  flush();

  const handleClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key, { replace: true });
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
        <Sider
          theme="light"
          width={264}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          style={{
            borderRight: `1px solid ${NEUTRAL_200}`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: collapsed ? '16px 8px' : '20px 16px',
            borderBottom: `1px solid ${NEUTRAL_200}`,
          }}
          >
            <MainLogo size={collapsed ? 'sm' : 'md'} />
          </div>

          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={items}
            onClick={handleClick}
            style={{ borderInlineEnd: 'none', paddingTop: 8 }}
          />

          <div style={{ padding: 12, marginTop: 8 }}>
            <Button
              block
              icon={<LogoutIcon fontSize="small" />}
              onClick={() => authAction.logout()}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                color: BBA_TERTIARY,
                borderColor: NEUTRAL_200,
              }}
            >
              {!collapsed && 'Log out'}
            </Button>
          </div>
        </Sider>

        <Layout style={{ background: '#F8FAFC' }}>
          <Content style={{ overflow: 'auto', padding: '40px 32px' }}>
            <div style={{ maxWidth: 1400, margin: '0 auto' }}>
              <Outlet />
            </div>
          </Content>
        </Layout>
      </Layout>
  );
};

export default DefaultLayout;
